/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import {
  GEOWEB_ROLE_PRESETS_ADMIN,
  useAuthenticationContext,
} from '@opengeoweb/authentication';
import { usePrevious } from '@opengeoweb/shared';
import { workspaceListActions } from '../store/workspaceList/reducer';
import { PresetFilterType } from '../store/workspaceList/types';

/**
 * Resets workspace list filter chips when the user's role changes
 * between admin and non-admin.
 */
export const useResetWorkspaceFiltersOnRoleChange = (): void => {
  const dispatch = useDispatch();
  const { currentRole } = useAuthenticationContext();
  const isAdmin = currentRole?.name === GEOWEB_ROLE_PRESETS_ADMIN.name;
  const lastAdmin = usePrevious(isAdmin);

  useEffect(() => {
    if (isAdmin !== lastAdmin && lastAdmin !== undefined) {
      const filters = [
        {
          id: PresetFilterType.system,
          isSelected: true,
        },
        {
          id: PresetFilterType.personal,
          isSelected: !isAdmin,
        },
        {
          id: PresetFilterType.shared,
          isSelected: true,
        },
      ];
      dispatch(workspaceListActions.setFilterChips(filters));
    }
  }, [dispatch, isAdmin, lastAdmin]);
};
